import React, {ReactElement, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Formik} from 'formik';
import {cx} from '@emotion/css';

import Button, {ButtonVariant} from '@fo/designSystem/Buttons/Button/Button';
import DynamicLink from '@fo/components/DynamicLink/DynamicLink';
import EmailWithFormikField from '@fo/designSystem/Fields/Email/EmailWithFormikField';
import CheckboxWithFormikField from '@fo/designSystem/Fields/Checkbox/CheckboxWithFormikField';
import {getPageRoute} from '@fo/utils/routes/getPageRoute';
import useMarketingSignup from '@fo/shared-data/useMarketingSignup';

import styles from './index.styles';

interface IMarketingSignupEmailCaptureForm {
  onSubmit?(isSubmitted: boolean): void;
}

type TMarketingSignupEmailCaptureForm = {
  email: string;
  confirm: boolean;
};

function MarketingSignupEmailCaptureForm({onSubmit}: IMarketingSignupEmailCaptureForm): ReactElement {
  const {formatMessage} = useIntl();
  const {submitContact, isContactSubmitted, isError} = useMarketingSignup();
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (onSubmit && isContactSubmitted) {
      onSubmit(isContactSubmitted);
    }
  }, [isContactSubmitted]);

  return (
    <Formik
      validateOnChange={false}
      validateOnBlur
      initialValues={{email: '', confirm: false}}
      onSubmit={(values) => submitContact(values as TMarketingSignupEmailCaptureForm)}
    >
      {({handleSubmit, values}) => (
        <>
          <EmailWithFormikField<TMarketingSignupEmailCaptureForm>
            label={formatMessage({id: 'signup.modal.fieldLabel'})}
            name="email"
            placeholder={formatMessage({id: 'signup.modal.fieldPlaceholder'})}
            required
          />
          <CheckboxWithFormikField<TMarketingSignupEmailCaptureForm>
            label={
              <FormattedMessage
                id="signup.modal.confirm"
                values={{
                  'a-terms': (...chunks: string[]) => (
                    <DynamicLink blank href={getPageRoute('terms')}>
                      {chunks}
                    </DynamicLink>
                  ),
                  'a-privacy': (...chunks: string[]) => (
                    <DynamicLink href={getPageRoute('privacyPolicy')} blank>
                      {chunks}
                    </DynamicLink>
                  ),
                }}
              />
            }
            name="confirm"
          />
          <div className={cx(styles.buttonContainer)}>
            <Button
              variant={ButtonVariant.PRIMARY}
              type="submit"
              ariaLabel={formatMessage({id: 'signup.modal.button'})}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
                setIsSubmitButtonDisabled(true);
              }}
              isDisabled={(isSubmitButtonDisabled || !values.confirm || values.email === '') && !isError}
            >
              <FormattedMessage id="subscribeToOurNewsletterSection.form.subscribeButton" />
            </Button>
          </div>
        </>
      )}
    </Formik>
  );
}

export default MarketingSignupEmailCaptureForm;
